'use client';

import { useEffect } from 'react';
import posthog from 'posthog-js';
import PageShell from '@/components/PageShell';
import Button from '@/components/ui/Button';
import { isStaleChunkError } from '@/lib/stale-chunk';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    try {
      posthog.captureException(error, {
        source: 'app/error.tsx',
        digest: error.digest,
      });
      // Any session that lands here is one we want to SEE. Force-start
      // replay (true overrides sampling and the landing-route recording
      // opt-out) so the error's aftermath, device, and journey are
      // captured even for sessions that were sampled out. Costs nothing
      // on healthy sessions; this only runs on the error screen.
      posthog.startSessionRecording(true);
    } catch {
      // never let analytics break the error UI
    }
  }, [error]);

  useEffect(() => {
    if (!isStaleChunkError(error)) return;
    // A tab from before the last deploy requested a chunk that no longer
    // exists; one reload picks up the new build manifest. The timestamp
    // keeps a genuinely broken build from reload-looping, and storage
    // access can itself throw (private mode), so those users just keep
    // the manual UI below.
    try {
      const KEY = 'chunk-reload-at';
      const last = Number(sessionStorage.getItem(KEY) || '0');
      if (Date.now() - last < 60_000) return;
      sessionStorage.setItem(KEY, String(Date.now()));
      window.location.reload();
    } catch {
      // fall through to the manual error UI
    }
  }, [error]);

  return (
    <PageShell maxWidth="xl">
      <main className="flex min-h-[80vh] items-center justify-center">
        <div className="w-full rounded-2xl bg-surface px-6 py-10 text-center shadow-card md:px-10 md:py-12">
          <h1 className="text-3xl font-bold text-text md:text-4xl">Something went wrong</h1>
          <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-text-secondary">
            We hit an unexpected error, and the team has been notified. You can try loading the
            page again, or go back to the home page.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button size="lg" onClick={() => reset()}>
              Try again
            </Button>
            <Button href="/" variant="secondary" size="lg">
              Go to the home page
            </Button>
          </div>
          {error.digest && (
            <p className="mt-6 text-xs text-text-muted">Error ID: {error.digest}</p>
          )}
        </div>
      </main>
    </PageShell>
  );
}
